import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getClient, updateClient, getProjects } from "../api/api";
import toast from "react-hot-toast";
import "./ClientDetail.css";
import FormGuidelines from "../components/FormGuidelines";

// ── Social config ─────────────────────────────────────────────
const SOCIAL_CONFIG = {
  facebook:  { label: "Facebook",  icon: "f",  color: "#3b82f6", bg: "#1d3461" },
  instagram: { label: "Instagram", icon: "📷", color: "#e1306c", bg: "#3d1020" },
  whatsapp:  { label: "WhatsApp",  icon: "💬", color: "#25d366", bg: "#0d2e1a" },
  tiktok:    { label: "TikTok",    icon: "♪",  color: "#ffffff", bg: "#1a1a1a" },
  linkedin:  { label: "LinkedIn",  icon: "in", color: "#0a66c2", bg: "#0a2540" },
  snapchat:  { label: "Snapchat",  icon: "👻", color: "#fffc00", bg: "#2a2800" },
};

const SOCIAL_FIELDS = Object.keys(SOCIAL_CONFIG);

// ── File helpers ─────────────────────────────────────────────
const FILE_ICONS = {
  pdf:"📄", doc:"📝", docx:"📝",
  xls:"📊", xlsx:"📊",
  png:"🖼️", jpg:"🖼️", jpeg:"🖼️", gif:"🖼️", webp:"🖼️",
  zip:"🗜️", rar:"🗜️",
  mp4:"🎬", mov:"🎬", avi:"🎬",
  mp3:"🎵", wav:"🎵",
};
const getFileIcon  = (name = "") => FILE_ICONS[name.split(".").pop().toLowerCase()] ?? "📎";
const formatBytes  = (b) => b < 1024 ? b+" B" : b < 1048576 ? (b/1024).toFixed(1)+" KB" : (b/1048576).toFixed(1)+" MB";

// ── Misc helpers ─────────────────────────────────────────────
function getInitials(name = "") {
  return (name||"").split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2);
}
function getContact(client, type) {
  return client?.contactInfo?.find(c => c.type === type)?.value || "";
}

// ── ACCEPT string for attachments (images + docs) ─────────────
const ATTACHMENT_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.zip,.rar,.mp4,.mov,.mp3,.wav";

export default function ClientDetail() {
  const navigate    = useNavigate();
  const { id }      = useParams();
  const location    = useLocation();
  const contractRef = useRef();
  const attachRef   = useRef();
  const transactionRef = useRef();

  const { currentUser } = useAuth();
  const role        = currentUser?.role?.toLowerCase();
  const canFinance  = role === "founder" || role === "manager";
  const canAccess   = canFinance || role === "team_leader";

  const [client,             setClient]             = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [saveStatus,         setSaveStatus]         = useState("idle");
  const [pendingContract,    setPendingContract]    = useState(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]); // new transaction files
  const [allProjects,        setAllProjects]        = useState([]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getClient(id),
      getProjects(),
    ])
      .then(([clientRes, projectsRes]) => {
        const data = clientRes?.data || clientRes;
        if (!data.phone) {
          const phoneContact = data.contactInfo?.find(c => c.type === "phone");
          if (phoneContact) data.phone = phoneContact.value;
        }
        setClient(data);

        const pData = projectsRes?.data?.data ?? projectsRes?.data ?? projectsRes ?? [];
        setAllProjects(Array.isArray(pData) ? pData : []);
      })
      .catch(() => navigate("/clients"))
      .finally(() => setLoading(false));
  }, [id]);

  // ── Access guard ─────────────────────────────────────────────
  if (!canAccess) return (
    <div style={{ minHeight:"100vh", background:"#080e1a", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
      <div style={{ fontSize:48 }}>🚫</div>
      <div style={{ color:"white", fontSize:18, fontWeight:700, fontFamily:"Syne,sans-serif" }}>Access Denied</div>
      <div style={{ color:"#4b5563", fontSize:14 }}>You don't have permission to view this page.</div>
      <button onClick={() => navigate("/clients")} style={{ marginTop:8, padding:"10px 24px", background:"#facc15", color:"#0a0f1a", border:"none", borderRadius:8, fontWeight:700, cursor:"pointer" }}>
        ← Back to Clients
      </button>
    </div>
  );

  if (loading || !client) return (
    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", minHeight:"60vh" }}>
      <div style={{ color:"#4b5563", fontSize:15 }}>Loading…</div>
    </div>
  );

  // ── Derived financial values ─────────────────────────────────
  const agreedPrice = parseFloat(client.agreedPrice) || 0;
  const earned      = parseFloat(client.earned)      || 0;
  const remaining   = parseFloat(client.restToEarn)  || Math.max(agreedPrice - earned, 0);
  const progress    = agreedPrice > 0 ? Math.min((earned / agreedPrice) * 100, 100) : 0;

  const contract    = client.contract    || null;
  const attachments = client.attachments || [];

  // transactions stored as files on server
  const transactions = client.transactions || [];

  const updateLocal = patch => setClient(prev => ({ ...prev, ...patch }));

  // ── Save ─────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaveStatus("saving");
    try {
      const payload = {
        name:        client.name || "",
        email:       client.email || "",
        phone:       client.phone || "",
        company:     client.company || "",
        status:      client.status || "pending",
        notes:       client.notes || "",
        agreedPrice: client.agreedPrice || 0,
        earned:      client.earned || 0,
        contactInfo: [
          ...SOCIAL_FIELDS
            .filter(k => getContact(client, k))
            .map(k => ({ type: k, value: getContact(client, k) })),
          ...(client.phone ? [{ type: "phone", value: client.phone }] : []),
        ],
        // projects stored as array of IDs
        projects: (client.projects || []).map(p => p._id || p),
        transactions: client.transactions || [],
      };

      const updated = await updateClient(
        client._id || id,
        payload,
        pendingContract,
        pendingAttachments.length > 0 ? pendingAttachments : null,
        pendingTransactions.length > 0 ? pendingTransactions : null,
      );

      const serverData = updated?.data || updated;
      if (serverData?._id) {
        setClient(prev => ({ ...prev, ...serverData }));
      }

      setPendingContract(null);
      setPendingAttachments([]);
      setPendingTransactions([]);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2200);
    } catch (err) {
      setSaveStatus("idle");
      console.error("Save error:", err);
      toast.error(err?.response?.data?.message || "Failed to save.");
    }
  };

  // ── Contract pick ─────────────────────────────────────────────
  const handleContractPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingContract(file);
    e.target.value = "";
  };

  // ── Attachment pick (multi) ───────────────────────────────────
  const handleAttachmentPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingAttachments(prev => [...prev, ...files]);
    e.target.value = "";
  };

  const removePendingAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // ── Transaction file pick (multi) ────────────────────────────
  const handleTransactionPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingTransactions(prev => [...prev, ...files]);
    e.target.value = "";
  };

  const removePendingTransaction = (index) => {
    setPendingTransactions(prev => prev.filter((_, i) => i !== index));
  };

  // ── Project toggle ────────────────────────────────────────────
  const toggleProject = (projectId) => {
    const current = (client.projects || []).map(p => p._id || p);
    const exists  = current.includes(projectId);
    const updated = exists
      ? current.filter(id => id !== projectId)
      : [...current, projectId];
    // keep as objects if they were objects
    const updatedObjs = updated.map(pid => {
      const found = allProjects.find(p => p._id === pid);
      return found || pid;
    });
    updateLocal({ projects: updatedObjs });
  };

  const isProjectSelected = (projectId) => {
    return (client.projects || []).map(p => p._id || p).includes(projectId);
  };

  // ── Social href ───────────────────────────────────────────────
  const getSocialHref = (type, value) => {
    if (!value) return "#";
    if (type === "whatsapp") return `https://wa.me/${value.replace(/\D/g,"")}`;
    if (value.startsWith("http")) return value;
    return `https://${value}`;
  };

  const saveBtnClass =
    saveStatus === "saved"  ? "save-btn save-btn-saved"  :
    saveStatus === "saving" ? "save-btn save-btn-saving" :
                              "save-btn save-btn-idle";

  /* ══════════════════════════════════════════════════════════ */
  return (
    <div className="client-detail-page">

      <button className="back-btn" onClick={() => navigate("/clients")}>
        ← Back to Clients
      </button>

      <FormGuidelines />

      {/* ── Profile Card ── */}
      <div className="profile-card">
        <div className="profile-left">
          <div className="profile-avatar">{getInitials(client.name)}</div>
          <div>
            <div className="profile-name">{client.name}</div>
            <div className="profile-company">{client.company}</div>

            <div className="profile-edit-row">
              <div className="profile-input-wrap">
                <span>📧</span>
                <input
                  className="profile-input email"
                  value={client.email || ""}
                  onChange={e => updateLocal({ email: e.target.value })}
                  placeholder="Email"
                />
              </div>
              <div className="profile-input-wrap">
                <span>📞</span>
                <input
                  className="profile-input phone"
                  value={client.phone || ""}
                  onChange={e => updateLocal({ phone: e.target.value })}
                  placeholder="Phone"
                />
              </div>
              <select
                className={`profile-status-select status-${client.status || "active"}`}
                value={client.status || "active"}
                onChange={e => updateLocal({ status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {client.createdAt && (
              <div className="profile-start-date">
                📅 Since <b>{new Date(client.createdAt).toLocaleDateString()}</b>
              </div>
            )}

            {/* Social chips */}
            <div className="profile-social">
              {SOCIAL_FIELDS.map(type => {
                const val = getContact(client, type);
                if (!val) return null;
                const s = SOCIAL_CONFIG[type];
                return (
                  <a
                    key={type}
                    href={getSocialHref(type, val)}
                    target="_blank"
                    rel="noreferrer"
                    className="social-chip"
                    style={{ background: s.bg, color: s.color, borderColor: `${s.color}30` }}
                  >
                    <span className="chip-icon">{s.icon}</span>
                    {s.label}
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="profile-right">
          {canFinance && (
            <button className={saveBtnClass} onClick={handleSave} disabled={saveStatus==="saving"}>
              {saveStatus==="saved" ? "✓ Saved!" : saveStatus==="saving" ? "Saving…" : "💾 Save"}
            </button>
          )}
        </div>
      </div>

      {/* ── Detail Grid ── */}
      <div className="detail-grid">

        {/* Social Media Edit */}
        <div className="detail-card">
          <h3>Social Media</h3>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {SOCIAL_FIELDS.map(type => {
              const s   = SOCIAL_CONFIG[type];
              const val = getContact(client, type);
              return (
                <div key={type}>
                  <span className="card-label" style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <span style={{ color:s.color }}>{s.icon}</span> {s.label}
                  </span>
                  <input
                    className="card-input"
                    value={val}
                    placeholder={type==="whatsapp" ? "+201234567890" : `https://${type}.com/…`}
                    onChange={e => {
                      const newInfo = (client.contactInfo||[]).filter(c=>c.type!==type);
                      if (e.target.value) newInfo.push({ type, value: e.target.value });
                      updateLocal({ contactInfo: newInfo });
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Financial — founder & manager only */}
        {canFinance && (
          <div className="detail-card">
            <h3>Financial</h3>
            <div className="finance-grid">
              {[
                { label:"Agreed Price", value: agreedPrice ? agreedPrice.toLocaleString()+" EGP" : "—" },
                { label:"Earned",       value: earned      ? earned.toLocaleString()+" EGP"      : "—", cls:"green" },
                { label:"Remaining",    value: remaining   ? remaining.toLocaleString()+" EGP"   : "—", cls:"red" },
                { label:"Progress",     value: Math.round(progress)+"%" },
              ].map(({ label, value, cls }) => (
                <div className="finance-item" key={label}>
                  <div className="fi-label">{label}</div>
                  <div className={`fi-value ${cls||""}`}>{value}</div>
                </div>
              ))}
            </div>
            <div className="progress-bar-track">
              <div className="progress-bar-fill" style={{ width:`${progress}%` }} />
            </div>
            {[
              { label:"Agreed Price (EGP)", key:"agreedPrice" },
              { label:"Earned (EGP)",       key:"earned"      },
            ].map(({ label, key }) => (
              <div key={key} style={{ marginBottom:12 }}>
                <span className="card-label">{label}</span>
                <input
                  type="number"
                  className="card-input"
                  value={client[key] || ""}
                  placeholder="0"
                  onChange={e => updateLocal({ [key]: e.target.value })}
                />
              </div>
            ))}
          </div>
        )}

        {/* Contract */}
        <div className="detail-card">
          <h3>Contract</h3>

          {contract?.url && !pendingContract && (
            <div className="file-item" style={{ marginBottom:12 }}>
              <div className="file-info">
                <span className="file-icon">📄</span>
                <div>
                  <div className="file-name">Current Contract</div>
                  <div className="file-size" style={{ fontSize:11, color:"#4b5563" }}>
                    {new Date(contract.uploadedAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
              <div className="file-actions">
                <a
                  href={contract.url}
                  target="_blank"
                  rel="noreferrer"
                  className="file-action-btn"
                  style={{ textDecoration:"none" }}
                >
                  View
                </a>
                <button className="file-action-btn" onClick={() => contractRef.current.click()}>
                  Replace
                </button>
              </div>
            </div>
          )}

          {pendingContract && (
            <div className="file-item" style={{ marginBottom:12, border:"1px solid #facc1540" }}>
              <div className="file-info">
                <span className="file-icon">{getFileIcon(pendingContract.name)}</span>
                <div>
                  <div className="file-name">{pendingContract.name}</div>
                  <div className="file-size" style={{ color:"#facc15", fontSize:11 }}>
                    {formatBytes(pendingContract.size)} — will upload on save
                  </div>
                </div>
              </div>
              <button className="file-action-btn" onClick={() => setPendingContract(null)}>
                Cancel
              </button>
            </div>
          )}

          {!contract?.url && !pendingContract && (
            <div
              className="upload-drop-zone"
              onClick={() => contractRef.current.click()}
              style={{ marginBottom:12 }}
            >
              <div style={{ fontSize:28, marginBottom:6 }}>📄</div>
              <div>Click to upload contract</div>
            </div>
          )}

          {!pendingContract && (
            <button
              className="upload-btn"
              onClick={() => contractRef.current.click()}
              style={{ width:"100%" }}
            >
              {contract?.url ? "Replace Contract" : "+ Upload Contract"}
            </button>
          )}

          <input
            ref={contractRef}
            type="file"
            hidden
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleContractPick}
          />
        </div>

        {/* Attachments */}
        <div className="detail-card">
          <h3>Attachments</h3>

          {attachments.length > 0 && (
            <div className="file-list" style={{ marginBottom:12 }}>
              {attachments.map((att, i) => (
                <div className="file-item" key={att._id || i}>
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(att.name || att.url || "")}</span>
                    <div>
                      <div className="file-name">
                        {att.name || att.url?.split("/").pop()?.split("?")[0] || `Attachment ${i + 1}`}
                      </div>
                      {att.size && (
                        <div className="file-size" style={{ fontSize:11, color:"#4b5563" }}>
                          {formatBytes(att.size)}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="file-actions">
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noreferrer"
                      className="file-action-btn"
                      style={{ textDecoration:"none" }}
                    >
                      View
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}

          {pendingAttachments.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, color:"#facc15", marginBottom:6, fontWeight:600 }}>
                Pending upload ({pendingAttachments.length} file{pendingAttachments.length > 1 ? "s" : ""}):
              </div>
              {pendingAttachments.map((file, i) => (
                <div className="file-item" key={i} style={{ marginBottom:6, border:"1px solid #facc1530" }}>
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(file.name)}</span>
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div className="file-size" style={{ color:"#facc15", fontSize:11 }}>
                        {formatBytes(file.size)} — will upload on save
                      </div>
                    </div>
                  </div>
                  <button className="file-action-btn" onClick={() => removePendingAttachment(i)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {attachments.length === 0 && pendingAttachments.length === 0 && (
            <div
              className="upload-drop-zone"
              onClick={() => attachRef.current.click()}
              style={{ marginBottom:12 }}
            >
              <div style={{ fontSize:28, marginBottom:6 }}>📎</div>
              <div>Click to upload attachments</div>
              <div style={{ fontSize:11, color:"#4b5563", marginTop:4 }}>
                Images, PDFs, docs, and more
              </div>
            </div>
          )}

          <button className="upload-btn" onClick={() => attachRef.current.click()} style={{ width:"100%" }}>
            + Add Attachment
          </button>

          <input
            ref={attachRef}
            type="file"
            hidden
            multiple
            accept={ATTACHMENT_ACCEPT}
            onChange={handleAttachmentPick}
          />
        </div>

        {/* Notes */}
        <div className="detail-card full-width">
          <h3>Notes</h3>
          <textarea
            className="notes-textarea"
            placeholder="Add any notes about this client…"
            value={client.notes || ""}
            onChange={e => updateLocal({ notes: e.target.value })}
          />
        </div>

        {/* ── Projects — referenced from project list ── */}
        <div className="detail-card full-width">
          <h3>Projects</h3>
          {allProjects.length === 0 ? (
            <div style={{ color:"#4b5563", fontSize:13 }}>No projects available.</div>
          ) : (
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {allProjects.map(project => {
                const selected = isProjectSelected(project._id);
                return (
                  <button
                    key={project._id}
                    onClick={() => toggleProject(project._id)}
                    style={{
                      padding:"8px 16px",
                      borderRadius:8,
                      border: selected ? "1px solid #facc15" : "1px solid #1e2a45",
                      background: selected ? "#facc1520" : "#0d1526",
                      color: selected ? "#facc15" : "#6b7280",
                      fontSize:13,
                      fontWeight: selected ? 600 : 400,
                      cursor:"pointer",
                      transition:"all 0.15s",
                      display:"flex",
                      alignItems:"center",
                      gap:6,
                    }}
                  >
                    {selected && <span>✓</span>}
                    {project.title || project.name}
                  </button>
                );
              })}
            </div>
          )}
          {(client.projects || []).length > 0 && (
            <div style={{ marginTop:10, fontSize:12, color:"#4b5563" }}>
              {(client.projects || []).length} project{(client.projects || []).length > 1 ? "s" : ""} selected
            </div>
          )}
        </div>

        {/* Tasks */}
        {client.tasks && client.tasks.length > 0 && (
          <div className="detail-card full-width">
            <h3>Tasks</h3>
            <div className="tasks-list">
              {client.tasks.map((task, i) => (
                <div key={task._id || i} className="task-item">
                  <div className="task-title">{task.title || `Task ${i + 1}`}</div>
                  <div className="task-status">{task.status || "Unknown"}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Transactions — file upload ── */}
        <div className="detail-card full-width">
          <h3>Transactions</h3>

          {/* Existing transaction files from server */}
          {transactions.length > 0 && (
            <div className="file-list" style={{ marginBottom:12 }}>
              {transactions.map((tr, i) => {
                // support both old (amount/date) format and new file format
                const isFile = tr.url || tr.fileUrl;
                if (isFile) {
                  const url  = tr.url || tr.fileUrl;
                  const name = tr.name || url?.split("/").pop()?.split("?")[0] || `Transaction ${i + 1}`;
                  return (
                    <div className="file-item" key={tr._id || i}>
                      <div className="file-info">
                        <span className="file-icon">{getFileIcon(name)}</span>
                        <div>
                          <div className="file-name">{name}</div>
                          {tr.uploadedAt && (
                            <div className="file-size" style={{ fontSize:11, color:"#4b5563" }}>
                              {new Date(tr.uploadedAt).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="file-actions">
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="file-action-btn"
                          style={{ textDecoration:"none" }}
                        >
                          View
                        </a>
                      </div>
                    </div>
                  );
                }
                // legacy amount/date display (read-only)
                return (
                  <div className="file-item" key={tr._id || i}>
                    <div className="file-info">
                      <span className="file-icon">💰</span>
                      <div>
                        <div className="file-name">{tr.amount ? `${Number(tr.amount).toLocaleString()} EGP` : "—"}</div>
                        {tr.date && (
                          <div className="file-size" style={{ fontSize:11, color:"#4b5563" }}>
                            {new Date(tr.date).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pending transaction files */}
          {pendingTransactions.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, color:"#facc15", marginBottom:6, fontWeight:600 }}>
                Pending upload ({pendingTransactions.length} file{pendingTransactions.length > 1 ? "s" : ""}):
              </div>
              {pendingTransactions.map((file, i) => (
                <div className="file-item" key={i} style={{ marginBottom:6, border:"1px solid #facc1530" }}>
                  <div className="file-info">
                    <span className="file-icon">{getFileIcon(file.name)}</span>
                    <div>
                      <div className="file-name">{file.name}</div>
                      <div className="file-size" style={{ color:"#facc15", fontSize:11 }}>
                        {formatBytes(file.size)} — will upload on save
                      </div>
                    </div>
                  </div>
                  <button className="file-action-btn" onClick={() => removePendingTransaction(i)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Upload zone */}
          {transactions.length === 0 && pendingTransactions.length === 0 && (
            <div
              className="upload-drop-zone"
              onClick={() => transactionRef.current.click()}
              style={{ marginBottom:12 }}
            >
              <div style={{ fontSize:28, marginBottom:6 }}>💰</div>
              <div>Click to upload transaction files</div>
              <div style={{ fontSize:11, color:"#4b5563", marginTop:4 }}>
                Receipts, invoices, PDFs, images
              </div>
            </div>
          )}

          <button
            className="upload-btn"
            onClick={() => transactionRef.current.click()}
            style={{ width:"100%" }}
          >
            + Add Transaction File
          </button>

          <input
            ref={transactionRef}
            type="file"
            hidden
            multiple
            accept={ATTACHMENT_ACCEPT}
            onChange={handleTransactionPick}
          />
        </div>

      </div>
    </div>
  );
}